import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Badge, Space, Typography, Popconfirm, message } from 'antd';
import { EditOutlined, StopOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ncfSequenceService, ncfTypeService, type NcfSequence, type NcfType } from '../../../services/ncfService';
import dayjs from 'dayjs';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const { Title } = Typography;

const NcfSequenceListPage: React.FC = () => {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState<NcfSequence[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [seqResult] = await Promise.all([
        ncfSequenceService.getAll(),
        ncfTypeService.getActive(),
      ]);
      setSequences(seqResult.data ?? []);
    } catch {
      message.error('Error al cargar las secuencias NCF');
      setSequences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleActive = async (id: number) => {
    try {
      await ncfSequenceService.toggleActive(id);
      message.success('Estado actualizado correctamente');
      load();
    } catch {
      message.error('Error al actualizar el estado');
    }
  };

  const isNearExhaustion = (seq: NcfSequence): boolean => {
    const range = seq.end_number - seq.start_number;
    const used = seq.current_number - seq.start_number;
    const percentage = (used / range) * 100;
    return seq.is_active && percentage > 90;
  };

  const isNearExpiry = (seq: NcfSequence): boolean => {
    if (!seq.is_active) return false;
    const expiryDate = dayjs(seq.valid_until);
    const today = dayjs();
    const daysUntilExpiry = expiryDate.diff(today, 'day');
    return daysUntilExpiry < 30 && daysUntilExpiry > 0;
  };

  const columns: ColumnsType<NcfSequence> = [
    {
      title: 'Tipo NCF',
      key: 'ncf_type',
      render: (_, record) => (
        <span>
          <strong>{record.ncf_type?.code}</strong> — {record.ncf_type?.name}
        </span>
      ),
    },
    {
      title: 'Prefijo',
      dataIndex: 'prefix',
      key: 'prefix',
    },
    {
      title: 'Rango',
      key: 'range',
      render: (_, record) => (
        <span>
          {record.prefix}{String(record.start_number).padStart(10, '0')} — {record.prefix}{String(record.end_number).padStart(10, '0')}
        </span>
      ),
    },
    {
      title: 'Actual',
      dataIndex: 'current_number',
      key: 'current_number',
      render: (current, record) => (
        <span>
          {record.prefix}{String(current).padStart(10, '0')}
          {isNearExhaustion(record) && (
            <Badge
              count="Agotamiento"
              style={{ backgroundColor: 'var(--ec-warning)', marginLeft: 8 }}
            />
          )}
        </span>
      ),
    },
    {
      title: 'Vigencia',
      key: 'validity',
      render: (_, record) => (
        <span>
          {dayjs(record.valid_from).format('DD/MM/YYYY')} — {dayjs(record.valid_until).format('DD/MM/YYYY')}
          {isNearExpiry(record) && (
            <Badge
              count="Vencimiento"
              style={{ backgroundColor: 'var(--ec-warning)', marginLeft: 8 }}
            />
          )}
        </span>
      ),
    },
    {
      title: 'Estado',
      key: 'is_active',
      render: (_, record) => (
        <Badge
          status={record.is_active ? 'success' : 'default'}
          text={record.is_active ? 'Activo' : 'Inactivo'}
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/ncf-sequences/${record.id}/edit`)}
            title="Editar"
          />
          <Popconfirm
            title={record.is_active ? '¿Desactivar esta secuencia?' : '¿Reactivar esta secuencia?'}
            onConfirm={() => handleToggleActive(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger={record.is_active}
              icon={record.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              title={record.is_active ? 'Desactivar' : 'Reactivar'}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <Breadcrumb items={[{ label: 'Inicio', to: '/admin-dashboard' }, { label: 'Secuencias NCF' }]} />
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col">
              <Title level={4}>Secuencias NCF ({sequences.length})</Title>
            </div>
            <div className="col-auto">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate('/ncf-sequences/new')}
              >
                Nueva Secuencia
              </Button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <Table
              columns={columns}
              dataSource={sequences}
              rowKey="id"
              loading={loading}
              locale={{ emptyText: 'No hay secuencias NCF registradas' }}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NcfSequenceListPage;
